/**
 * Category Form Dialog Component
 * Dialog for creating and editing adventure categories
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Box,
  IconButton,
  Alert,
  Typography,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Popover,
  Paper,
  ListItemText,
} from '@mui/material'
import {
  Close as CloseIcon,
  IndeterminateCheckBox as IndeterminateIcon,
  EmojiEmotions as EmojiIcon,
} from '@mui/icons-material'
import EmojiPicker, { EmojiClickData, Theme, EmojiStyle } from 'emoji-picker-react'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  useCreateCategory,
  useUpdateCategory,
  useUserFlags,
  useCriteriaTypes,
  useNodeTypes,
} from '../../hooks/useAdventureCategories'
import type {
  AdventureCategoryDto,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CriteriaTypeDto,
} from '@api/types/adventureCategory'

interface CategoryFormDialogProps {
  open: boolean
  onClose: () => void
  category?: AdventureCategoryDto | null
}

type TriState = true | false | null

const TriStateSwitch = ({
  label,
  value,
  onChange,
  disabled,
  helperText,
}: {
  label: string
  value: TriState
  onChange: (value: TriState) => void
  disabled?: boolean
  helperText?: string
}) => {
  const handleClick = () => {
    // Cycle through: true -> false -> null -> true
    if (value === true) onChange(false)
    else if (value === false) onChange(null)
    else onChange(true)
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={value === true}
            indeterminate={value === null}
            onChange={handleClick}
            disabled={disabled}
            icon={value === null ? <IndeterminateIcon color="action" /> : undefined}
          />
        }
        label={label}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
          {helperText} {value === null && '(Inherit default)'}
        </Typography>
      )}
    </Box>
  )
}

export function CategoryFormDialog({ open, onClose, category }: CategoryFormDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const isEditing = !!category

  // Form state - basic
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [iconName, setIconName] = useState('')
  const [color, setColor] = useState('#1976d2')
  const [sortOrder, setSortOrder] = useState(0)
  const [workflowAllowed, setWorkflowAllowed] = useState(true)
  const [workflowRequired, setWorkflowRequired] = useState(false)
  const [isPublicAllowed, setIsPublicAllowed] = useState<TriState>(true)
  const [groupsAllowed, setGroupsAllowed] = useState<TriState>(true)
  const [repeatableAllowed, setRepeatableAllowed] = useState<TriState>(true)
  const [isActive, setIsActive] = useState(true)

  // Form state - new fields
  const [statisticTypes, setStatisticTypes] = useState<number>(0) // 0=None, 1=Leaderboard, 2=CheckIn
  const [requiredUserFlags, setRequiredUserFlags] = useState<number>(0)
  const [allowedNodes, setAllowedNodes] = useState<number[]>([])
  const [allowedJoinCriteria, setAllowedJoinCriteria] = useState<number[]>([])
  const [allowedDisplayCriteria, setAllowedDisplayCriteria] = useState<number[]>([])
  const [allowedLogicCriteria, setAllowedLogicCriteria] = useState<number[]>([])

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null)
  const emojiPickerOpen = Boolean(emojiAnchorEl)

  // Fetch reference data
  const { data: userFlags, isLoading: isLoadingFlags } = useUserFlags()
  const { data: criteriaTypes, isLoading: isLoadingCriteria } = useCriteriaTypes()
  const { data: nodeTypes, isLoading: isLoadingNodes } = useNodeTypes()

  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory()

  const isPending = createMutation.isPending || updateMutation.isPending
  const isLoadingRef = isLoadingFlags || isLoadingCriteria || isLoadingNodes

  // Group criteria types by category
  const criteriaByCategory = useMemo(() => {
    if (!criteriaTypes) return {}
    return criteriaTypes.reduce((acc, ct) => {
      const cat = ct.category || 'Other'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(ct)
      return acc
    }, {} as Record<string, CriteriaTypeDto[]>)
  }, [criteriaTypes])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (category) {
        setName(category.name)
        setDisplayName(category.displayName)
        setDescription(category.description || '')
        setIconName(category.iconName || '')
        setColor(category.color || '#1976d2')
        setSortOrder(category.sortOrder)
        setWorkflowAllowed(category.workflowAllowed)
        setWorkflowRequired(category.workflowRequired)
        setIsPublicAllowed(category.isPublicAllowed)
        setGroupsAllowed(category.groupsAllowed)
        setRepeatableAllowed(category.repeatableAllowed)
        setIsActive(category.isActive)
        // New fields
        setStatisticTypes(category.statisticTypes)
        setRequiredUserFlags(category.requiredUserFlags)
        setAllowedNodes(category.allowedNodes || [])
        setAllowedJoinCriteria(category.allowedJoinCriteria || [])
        setAllowedDisplayCriteria(category.allowedDisplayCriteria || [])
        setAllowedLogicCriteria(category.allowedLogicCriteria || [])
      } else {
        // Reset to defaults for new category
        setName('')
        setDisplayName('')
        setDescription('')
        setIconName('')
        setColor('#1976d2')
        setSortOrder(0)
        setWorkflowAllowed(true)
        setWorkflowRequired(false)
        setIsPublicAllowed(true)
        setGroupsAllowed(true)
        setRepeatableAllowed(true)
        setIsActive(true)
        // New fields
        setStatisticTypes(0)
        setRequiredUserFlags(0)
        setAllowedNodes([])
        setAllowedJoinCriteria([])
        setAllowedDisplayCriteria([])
        setAllowedLogicCriteria([])
      }
      setErrors({})
    }
  }, [open, category])

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length > 50) {
      newErrors.name = 'Name must be less than 50 characters'
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    } else if (displayName.length > 100) {
      newErrors.displayName = 'Display name must be less than 100 characters'
    }

    if (description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters'
    }

    if (workflowRequired && !workflowAllowed) {
      newErrors.workflow = 'Workflow must be allowed if it is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    try {
      if (isEditing && category) {
        const data: UpdateCategoryRequest = {
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim() || null,
          iconName: iconName.trim(),
          color: color || null,
          sortOrder,
          workflowAllowed,
          workflowRequired,
          isPublicAllowed,
          groupsAllowed,
          repeatableAllowed,
          isActive,
          // New fields
          statisticTypes,
          requiredUserFlags,
          allowedNodes: allowedNodes.length > 0 ? allowedNodes : null,
          allowedJoinCriteria: allowedJoinCriteria.length > 0 ? allowedJoinCriteria : null,
          allowedDisplayCriteria: allowedDisplayCriteria.length > 0 ? allowedDisplayCriteria : null,
          allowedLogicCriteria: allowedLogicCriteria.length > 0 ? allowedLogicCriteria : null,
        }
        await updateMutation.mutateAsync({ id: category.id, data })
      } else {
        const data: CreateCategoryRequest = {
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim() || null,
          iconName: iconName.trim(),
          color: color || null,
          sortOrder,
          workflowAllowed,
          workflowRequired,
          isPublicAllowed,
          groupsAllowed,
          repeatableAllowed,
          isActive,
          // New fields
          statisticTypes,
          requiredUserFlags,
          allowedNodes: allowedNodes.length > 0 ? allowedNodes : null,
          allowedJoinCriteria: allowedJoinCriteria.length > 0 ? allowedJoinCriteria : null,
          allowedDisplayCriteria: allowedDisplayCriteria.length > 0 ? allowedDisplayCriteria : null,
          allowedLogicCriteria: allowedLogicCriteria.length > 0 ? allowedLogicCriteria : null,
        }
        await createMutation.mutateAsync(data)
      }
      onClose()
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        submit: err instanceof Error ? err.message : 'Failed to save category',
      }))
    }
  }

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setIconName(emojiData.emoji)
    setEmojiAnchorEl(null)
  }

  const handleClearEmoji = () => {
    setIconName('')
  }

  const renderCriteriaMultiSelect = (
    label: string,
    value: number[],
    onChange: (ids: number[]) => void
  ) => {
    const allIds = criteriaTypes?.map((c) => c.id) || []
    const allSelected = allIds.length > 0 && allIds.every((id) => value.includes(id))

    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={value}
            onChange={(e) => onChange(e.target.value as number[])}
            input={<OutlinedInput label={label} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const ct = criteriaTypes?.find((c) => c.id === id)
                  return <Chip key={id} label={ct?.name || id} size="small" />
                })}
              </Box>
            )}
            disabled={isPending}
          >
            {Object.entries(criteriaByCategory).map(([cat, types]) => [
              <MenuItem key={`header-${cat}`} disabled sx={{ fontWeight: 'bold', opacity: '1 !important' }}>
                {cat}
              </MenuItem>,
              ...types.map((type) => (
                <MenuItem key={String(type.id)} value={type.id} sx={{ pl: 2 }}>
                  <Checkbox checked={value.indexOf(type.id) > -1} size="small" />
                  <ListItemText primary={type.name} />
                </MenuItem>
              )),
            ])}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChange(allSelected ? [] : allIds)}
          disabled={isPending || !criteriaTypes?.length}
          sx={{ minWidth: 'auto', whiteSpace: 'nowrap', mt: 0.5 }}
        >
          {allSelected ? 'Clear' : 'All'}
        </Button>
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth {...mobileDialogProps}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isEditing ? 'Edit Category' : 'Create Category'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isPending}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrors(prev => ({ ...prev, submit: '' }))}>
            {errors.submit}
          </Alert>
        )}

        {isLoadingRef && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name || 'Internal identifier (e.g., traditional_geocache)'}
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Name"
                  required
                  fullWidth
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  error={!!errors.displayName}
                  helperText={errors.displayName || 'Shown to users (e.g., Traditional GeoCaches)'}
                  disabled={isPending}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description || `${description.length}/500`}
                  disabled={isPending}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Appearance */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Appearance
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Icon (Emoji) - Optional
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Paper
                    variant="outlined"
                    onClick={(e) => !isPending && setEmojiAnchorEl(e.currentTarget)}
                    sx={{
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isPending ? 'default' : 'pointer',
                      fontSize: '2rem',
                      '&:hover': {
                        backgroundColor: isPending ? 'transparent' : 'action.hover',
                      },
                    }}
                  >
                    {iconName || <EmojiIcon color="disabled" />}
                  </Paper>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => !isPending && setEmojiAnchorEl(e.currentTarget)}
                      disabled={isPending}
                    >
                      {iconName ? 'Change' : 'Select'}
                    </Button>
                    {iconName && (
                      <Button
                        size="small"
                        color="error"
                        onClick={handleClearEmoji}
                        disabled={isPending}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {iconName ? 'Click to change or remove' : 'No icon (optional)'}
                </Typography>
                <Popover
                  open={emojiPickerOpen}
                  anchorEl={emojiAnchorEl}
                  onClose={() => setEmojiAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    theme={Theme.AUTO}
                    emojiStyle={EmojiStyle.NATIVE}
                    width="min(350px, calc(100vw - 32px))"
                    height={400}
                  />
                </Popover>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Color"
                  fullWidth
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  disabled={isPending}
                  InputProps={{
                    sx: { height: 56 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Sort Order"
                  fullWidth
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                  disabled={isPending}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Workflow Settings */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Workflow Settings
            </Typography>
            {errors.workflow && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.workflow}
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={workflowAllowed}
                      onChange={(e) => {
                        setWorkflowAllowed(e.target.checked)
                        if (!e.target.checked) {
                          setWorkflowRequired(false)
                        }
                      }}
                      disabled={isPending}
                    />
                  }
                  label="Workflow Allowed"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                  Adventures in this category can have workflows
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={workflowRequired}
                      onChange={(e) => setWorkflowRequired(e.target.checked)}
                      disabled={isPending || !workflowAllowed}
                    />
                  }
                  label="Workflow Required"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                  Adventures in this category must have a workflow
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Feature Flags (with tri-state) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Feature Flags
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Check = Allowed, Unchecked = Disallowed, Indeterminate = Use Default
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TriStateSwitch
                  label="Public Allowed"
                  value={isPublicAllowed}
                  onChange={setIsPublicAllowed}
                  disabled={isPending}
                  helperText="Adventures can be made publicly visible"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TriStateSwitch
                  label="Groups Allowed"
                  value={groupsAllowed}
                  onChange={setGroupsAllowed}
                  disabled={isPending}
                  helperText="Adventures can be associated with groups"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TriStateSwitch
                  label="Repeatable Allowed"
                  value={repeatableAllowed}
                  onChange={setRepeatableAllowed}
                  disabled={isPending}
                  helperText="Adventures can be marked as repeatable"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={isPending}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                  Category is available for use
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Statistics Settings */}
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Statistics Settings
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Statistic Type</InputLabel>
              <Select
                value={statisticTypes}
                onChange={(e) => setStatisticTypes(Number(e.target.value))}
                label="Statistic Type"
                disabled={isPending}
              >
                <MenuItem value={0}>None</MenuItem>
                <MenuItem value={1}>Leaderboard</MenuItem>
                <MenuItem value={2}>Check In</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Determines how adventures in this category track completion/progress
            </Typography>
          </Box>

          {/* Access Control - Required User Flags */}
          <FormControl fullWidth size="small">
            <InputLabel>Required User Flags</InputLabel>
            <Select
              value={requiredUserFlags}
              onChange={(e) => setRequiredUserFlags(Number(e.target.value))}
              input={<OutlinedInput label="Required User Flags" />}
              disabled={isPending || isLoadingFlags}
            >
              <MenuItem value={0}>
                <em>None</em>
              </MenuItem>
              {userFlags?.map((flag) => (
                <MenuItem key={flag.id} value={flag.id}>
                  {flag.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Allowed Node Types */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Allowed Nodes</InputLabel>
              <Select
                multiple
                value={allowedNodes}
                onChange={(e) => setAllowedNodes(e.target.value as number[])}
                input={<OutlinedInput label="Allowed Nodes" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const nt = nodeTypes?.find((n) => n.id === id)
                      return <Chip key={id} label={nt?.displayName || nt?.name || id} size="small" />
                    })}
                  </Box>
                )}
                disabled={isPending || isLoadingNodes}
              >
                {nodeTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    <Checkbox checked={allowedNodes.indexOf(type.id) > -1} size="small" />
                    <ListItemText primary={type.displayName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const allIds = nodeTypes?.map((n) => n.id) || []
                const allSelected = allIds.length > 0 && allIds.every((id) => allowedNodes.includes(id))
                setAllowedNodes(allSelected ? [] : allIds)
              }}
              disabled={isPending || isLoadingNodes || !nodeTypes?.length}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap', mt: 0.5 }}
            >
              {nodeTypes && nodeTypes.length > 0 && nodeTypes.every((n) => allowedNodes.includes(n.id)) ? 'Clear' : 'All'}
            </Button>
          </Box>

          {/* Allowed Criteria */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {renderCriteriaMultiSelect(
              'Allowed Join Criteria',
              allowedJoinCriteria,
              setAllowedJoinCriteria
            )}
            {renderCriteriaMultiSelect(
              'Allowed Display Criteria',
              allowedDisplayCriteria,
              setAllowedDisplayCriteria
            )}
            {renderCriteriaMultiSelect(
              'Allowed Logic Criteria',
              allowedLogicCriteria,
              setAllowedLogicCriteria
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending || !name.trim() || !displayName.trim()}
          startIcon={isPending ? <CircularProgress size={20} /> : null}
        >
          {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
